import { unwrap, type components } from "@sagot/api-client";
import { useMutation } from "@tanstack/react-query";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { saveBlob } from "../../../engage/shared/download";
import { serviceKeys } from "../../service-keys";
import { useServiceMutation, useServiceQuery } from "../../use-service-query";

type Schemas = components["schemas"];
export type AccuracyOut = Schemas["AccuracyOut"];
export type AccuracyMonth = Schemas["AccuracyMonth"];
export type AccuracyCategory = Schemas["AccuracyCategory"];
export type CategorySettings = Schemas["CategorySettingsOut"];

/** The accuracy report is read often (chip, banner, tab); a minute old is fine. */
const ACCURACY_STALE_MS = 60_000;
/** How often the accuracy report is read again while a retrain runs. */
export const TRAINING_POLL_MS = 3_000;
/** Tickets listed at once in "Needs category". */
export const NEEDS_CATEGORY_PAGE = 50;
const FALLBACK_FILE = "category-accuracy.csv";

export const categorizationKeys = {
  all: (ws: string) => [...serviceKeys.all(ws), "categorization"] as const,
  accuracy: (ws: string) => [...categorizationKeys.all(ws), "accuracy"] as const,
  settings: (ws: string) => [...categorizationKeys.all(ws), "settings"] as const,
  needs: (ws: string) => [...categorizationKeys.all(ws), "needs-category"] as const,
};

/** `GET /v1/service-categorization/accuracy`: the monthly report, the auto-apply state, the threshold and the newest model. */
export function useAccuracy(enabled = true) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: categorizationKeys.accuracy(ws),
    enabled,
    staleTime: ACCURACY_STALE_MS,
    request: (api, signal) => api.GET("/v1/service-categorization/accuracy", { signal }),
  });
}

/** `GET /v1/tickets?needs_category=true`: open tickets the categorizer handled and nobody settled yet. */
export function useNeedsCategory() {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: categorizationKeys.needs(ws),
    request: (api, signal) => api.GET("/v1/tickets", { params: { query: { needs_category: true, open_only: true, limit: NEEDS_CATEGORY_PAGE } }, signal }),
  });
}

export function useCategorySettings() {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: categorizationKeys.settings(ws),
    request: (api, signal) => api.GET("/v1/service-categorization/settings", { signal }),
  });
}

export function useSaveCategorySettings() {
  return useServiceMutation({
    request: (api, body: { auto_apply: boolean; target_precision: number }) => api.PUT("/v1/service-categorization/settings", { body }),
    invalidate: (ws) => [categorizationKeys.settings(ws), categorizationKeys.accuracy(ws)],
  });
}

/** `POST /v1/service-categorization/retrain` (202): the model row is `training` until the worker has finished. */
export function useRetrain() {
  return useServiceMutation({
    request: (api, _none: undefined) => api.POST("/v1/service-categorization/retrain"),
    invalidate: (ws) => [categorizationKeys.accuracy(ws)],
  });
}

/** Settle a ticket's category with `PATCH /v1/tickets/{number}`: the suggested type confirms it, another type corrects it. */
export function useSettleCategory() {
  return useServiceMutation({
    request: (api, variables: { number: string; version: number; typeId: string }) =>
      api.PATCH("/v1/tickets/{number}", { params: { path: { number: variables.number } }, body: { version: variables.version, type_id: variables.typeId } }),
    invalidate: (ws) => [categorizationKeys.needs(ws), categorizationKeys.accuracy(ws), serviceKeys.tickets(ws)],
  });
}

/** The report as CSV, saved under the name the server gives it. */
export function useAccuracyExport() {
  const api = useApi();
  return useMutation({
    mutationFn: async () => {
      const result = await api.GET("/v1/service-categorization/accuracy/export", { parseAs: "blob" });
      const blob = await unwrap(Promise.resolve(result));
      const disposition = result.response.headers.get("Content-Disposition") ?? "";
      const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? FALLBACK_FILE;
      saveBlob(blob, filename);
      return filename;
    },
  });
}
